import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import './ProfilePage.css';

const roleLabels = {
  super_admin: 'प्रणाली प्रशासक', taluka_admin: 'तालुका प्रशासक',
  grampanchayat_admin: 'ग्रामपंचायत प्रशासक', department: 'विभाग कर्मचारी', citizen: 'नागरिक',
};

const formatDate = (value, withTime = false) => {
  if (!value) return 'उपलब्ध नाही';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'उपलब्ध नाही';
  return date.toLocaleString('mr-IN', withTime
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' });
};

function ProfileIcon({ name, size = 17 }) {
  const paths = {
    user: <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
    shield: <path d="M12 3 5 6v5c0 4.7 2.8 8.2 7 10 4.2-1.8 7-5.3 7-10V6l-7-3Zm-2 9 1.5 1.5L15 10"/>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H3v-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6V3h4v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></>,
    edit: <><path d="m14 5 5 5L8 21H3v-5L14 5Z"/><path d="m12 7 5 5"/></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/></>,
    building: <><path d="M4 21V8l8-5 8 5v13"/><path d="M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    save: <><path d="M5 3h12l3 3v15H4V3h1Z"/><path d="M8 3v6h8V3M8 21v-7h8v7"/></>,
    reset: <><path d="M4 4v6h6"/><path d="M5.5 16a8 8 0 1 0 .5-9l-2 3"/></>,
    phone: <path d="M6.6 3h3l1.5 4-2 1.3a16 16 0 0 0 6.6 6.6l1.3-2 4 1.5v3c0 2-1.6 3.6-3.6 3.6A14.4 14.4 0 0 1 3 6.6C3 4.6 4.6 3 6.6 3Z"/>,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></>,
    'eye-off': <><path d="m3 3 18 18"/><path d="M10.6 6.2A10.5 10.5 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-2.1 2.8M6.2 6.2C3.8 7.8 2.5 12 2.5 12s3.5 6 9.5 6a9.6 9.6 0 0 0 3.1-.5"/><path d="M9.8 9.8a3 3 0 0 0 4.2 4.2"/></>,
  };
  return <svg className="profile-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.user}</svg>;
}

export default function ProfilePage() {
  const { user, tenant, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(user || {});
  const [tab, setTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileForm, setProfileForm] = useState({ full_name: '', email: '', phone: '', username: '', avatar_url: '' });
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [phoneVerification, setPhoneVerification] = useState({
    otp: '', token: '', verifiedPhone: '', otpRequested: false, loading: false, message: '', error: '',
  });
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const applyProfile = (data) => {
    setProfile(data);
    setProfileForm({
      full_name: data.full_name || '', email: data.email || '', phone: data.phone || '',
      username: data.username || '', avatar_url: data.avatar_url || '',
    });
  };

  useEffect(() => {
    if (!user) return;
    authAPI.getProfile().then(({ data }) => applyProfile(data.user || user))
      .catch(() => setMessage({ type: 'error', text: 'प्रोफाइल माहिती लोड करता आली नाही.' }));
  }, [user]);

  const completionItems = useMemo(() => [
    ['वैयक्तिक माहिती', Boolean(profileForm.full_name)],
    ['संपर्क क्रमांक', Boolean(profileForm.phone)],
    ['ईमेल पत्ता', Boolean(profileForm.email)],
    ['प्रोफाइल फोटो', Boolean(profileForm.avatar_url)],
  ], [profileForm]);
  const completion = Math.round((completionItems.filter(([, done]) => done).length / completionItems.length) * 100);
  const roleLabel = roleLabels[profile.role] || (profile.role || '').replace(/_/g, ' ') || 'वापरकर्ता';
  const initials = (profileForm.full_name || profileForm.username || 'व').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();

  const updateProfile = async (event) => {
    event.preventDefault(); setLoading(true); setMessage({ type: '', text: '' });
    const normalizedPhone = profileForm.phone.replace(/\D/g, '');
    const phoneChanged = normalizedPhone !== String(profile.phone || '').replace(/\D/g, '');
    if (phoneChanged && (phoneVerification.verifiedPhone !== normalizedPhone || !phoneVerification.token)) {
      setLoading(false);
      setPhoneVerification(current => ({ ...current, error: 'नवीन मोबाइल क्रमांक OTP द्वारे पडताळा.' }));
      return;
    }
    try {
      const { data } = await authAPI.updateProfile({
        full_name: profileForm.full_name.trim(),
        phone: normalizedPhone,
        phone_verification_token: phoneChanged ? phoneVerification.token : undefined,
        avatar_url: profileForm.avatar_url.trim(),
      });
      const merged = { ...user, ...data.user };
      setUser(merged); localStorage.setItem('user', JSON.stringify(merged)); applyProfile(data.user);
      setPhoneVerification({ otp: '', token: '', verifiedPhone: '', otpRequested: false, loading: false, message: '', error: '' });
      setMessage({ type: 'success', text: 'प्रोफाइल यशस्वीरित्या अपडेट झाले.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'प्रोफाइल अपडेट करता आले नाही.' });
    } finally { setLoading(false); }
  };

  const requestPhoneOtp = async () => {
    const phone = profileForm.phone.replace(/\D/g, '');
    setPhoneVerification(current => ({ ...current, loading: true, error: '', message: '' }));
    try {
      const { data } = await authAPI.requestProfilePhoneOtp({ phone });
      setPhoneVerification(current => ({
        ...current, otp: '', token: '', verifiedPhone: '', otpRequested: true, loading: false,
        message: data.message || 'OTP नवीन मोबाइल क्रमांकावर पाठवला आहे.',
      }));
    } catch (error) {
      setPhoneVerification(current => ({
        ...current, loading: false, error: error.response?.data?.error || 'OTP पाठवता आला नाही.',
      }));
    }
  };

  const verifyPhoneOtp = async () => {
    const phone = profileForm.phone.replace(/\D/g, '');
    setPhoneVerification(current => ({ ...current, loading: true, error: '', message: '' }));
    try {
      const { data } = await authAPI.verifyProfilePhoneOtp({ phone, otp: phoneVerification.otp });
      setPhoneVerification(current => ({
        ...current,
        token: data.phone_verification_token,
        verifiedPhone: phone,
        otpRequested: false,
        loading: false,
        message: '✓ नवीन मोबाइल क्रमांक पडताळला आहे.',
      }));
    } catch (error) {
      setPhoneVerification(current => ({
        ...current, token: '', verifiedPhone: '', loading: false,
        error: error.response?.data?.error || 'OTP पडताळता आला नाही.',
      }));
    }
  };

  const changePassword = async (event) => {
    event.preventDefault(); setMessage({ type: '', text: '' });
    if (passwordForm.newPassword !== passwordForm.confirmPassword) return setMessage({ type: 'error', text: 'नवीन पासवर्ड जुळत नाहीत.' });
    if (passwordForm.newPassword.length < 6) return setMessage({ type: 'error', text: 'नवीन पासवर्ड किमान ६ अक्षरांचा असावा.' });
    setLoading(true);
    try {
      await authAPI.changePassword({ oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword });
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'पासवर्ड यशस्वीरित्या बदलला.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'पासवर्ड बदलता आला नाही.' });
    } finally { setLoading(false); }
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault(); setDeleteError('');
    if (!deletePassword.trim()) return setDeleteError('खाते हटवण्यासाठी कृपया तुमचा पासवर्ड टाका.');
    if (!deleteConfirmed) return setDeleteError('कृपया अटी स्वीकारण्यासाठी चेकबॉक्स निवडा.');
    setDeleteLoading(true);
    try {
      await authAPI.deleteAccount({ password: deletePassword, reason: deleteReason });
      alert('तुमचे खाते यशस्वीरीत्या कायमस्वरूपी बंद करण्यात आले आहे.');
      logout();
      navigate('/login');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'खाते हटवता आले नाही. कृपया योग्य पासवर्ड टाका.');
    } finally { setDeleteLoading(false); }
  };

  if (!user) return <div className="profile-shell"><div className="profile-empty">प्रोफाइल पाहण्यासाठी लॉगिन करा.</div></div>;

  return (
    <div className="profile-shell">
      <section className="profile-hero">
        <div className="profile-hero-identity">
          <div className="profile-avatar-large">
            {profileForm.avatar_url ? <img src={profileForm.avatar_url} alt="प्रोफाइल" /> : <span>{initials}</span>}
          </div>
          <div>
            <p className="profile-eyebrow">माझे खाते</p>
            <h1>{profileForm.full_name || profileForm.username}</h1>
            <p className="profile-hero-role">{roleLabel}</p>
            <p className="profile-hero-email"><ProfileIcon name="mail" size={15} /> {profileForm.email || 'ईमेल उपलब्ध नाही'}</p>
            <div className="profile-chips"><span><ProfileIcon name="building" size={14} /> {tenant?.name || profile.Tenant?.name || 'ग्रामपंचायत'}</span><span><ProfileIcon name="check" size={14} /> सक्रिय खाते</span></div>
          </div>
        </div>
        <div className="profile-hero-art" aria-hidden="true">⌂</div>
        <button className="profile-hero-action" onClick={() => { setTab('profile'); document.querySelector('.profile-tabs')?.scrollIntoView({ behavior: 'smooth' }); }}><ProfileIcon name="edit" /> संपर्क अपडेट करा</button>
      </section>

      <nav className="profile-tabs" aria-label="प्रोफाइल विभाग">
        <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}><ProfileIcon name="user" /> प्रोफाइल माहिती</button>
        <button className={tab === 'security' ? 'active' : ''} onClick={() => setTab('security')}><ProfileIcon name="shield" /> सुरक्षा</button>
        <button className={tab === 'account' ? 'active' : ''} onClick={() => setTab('account')}><ProfileIcon name="settings" /> खाते तपशील</button>
      </nav>

      {message.text && <div className={`profile-alert ${message.type}`}>{message.type === 'success' ? '✓' : '!'} {message.text}</div>}

      <div className="profile-layout">
        <main className="profile-main">
          {tab === 'profile' && <>
            <section className="profile-panel">
              <PanelTitle icon="user" title="वैयक्तिक माहिती" subtitle="तुमचे नाव आणि संपर्क तपशील अद्ययावत ठेवा" />
              <form className="profile-form" onSubmit={updateProfile}>
                <Field label="पूर्ण नाव" required value={profileForm.full_name} onChange={value => setProfileForm({ ...profileForm, full_name: value })} />
                <Field label="ईमेल (लॉगिन आयडी)" value={profileForm.email} disabled hint="ईमेल बदलण्यासाठी प्रणाली प्रशासकाशी संपर्क करा." />
                <Field label="वापरकर्ता नाव" value={profileForm.username} disabled hint="हा तुमचा कायमस्वरूपी लॉगिन आयडी आहे." />
                <label className="profile-field profile-phone-field">
                  <span>मोबाइल क्रमांक<em> *</em></span>
                  <div className="profile-phone-row">
                    <input
                      type="tel"
                      inputMode="numeric"
                      data-marathi-input="false"
                      value={profileForm.phone}
                      disabled={Boolean(phoneVerification.token)}
                      maxLength="10"
                      onChange={event => {
                        const phone = event.target.value.replace(/\D/g, '').slice(0, 10);
                        setProfileForm(current => ({ ...current, phone }));
                        setPhoneVerification({
                          otp: '', token: '', verifiedPhone: '', otpRequested: false, loading: false, message: '', error: '',
                        });
                      }}
                    />
                    {phoneVerification.token ? (
                      <button
                        type="button"
                        className="profile-phone-action verified"
                        onClick={() => setPhoneVerification(current => ({ ...current, token: '', verifiedPhone: '', message: '' }))}
                      >
                        ✓ पडताळले · बदला
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="profile-phone-action"
                        disabled={phoneVerification.loading || profileForm.phone.length !== 10 || profileForm.phone === String(profile.phone || '')}
                        onClick={requestPhoneOtp}
                      >
                        {phoneVerification.loading ? 'पाठवत आहे…' : phoneVerification.otpRequested ? 'OTP पुन्हा पाठवा' : 'OTP पाठवा'}
                      </button>
                    )}
                  </div>
                  {phoneVerification.otpRequested && !phoneVerification.token && (
                    <div className="profile-phone-otp">
                      <input
                        value={phoneVerification.otp}
                        inputMode="numeric"
                        data-marathi-input="false"
                        maxLength="6"
                        placeholder="६ अंकी OTP"
                        onChange={event => setPhoneVerification(current => ({
                          ...current, otp: event.target.value.replace(/\D/g, '').slice(0, 6), error: '',
                        }))}
                      />
                      <button type="button" disabled={phoneVerification.loading || phoneVerification.otp.length !== 6} onClick={verifyPhoneOtp}>
                        {phoneVerification.loading ? 'पडताळत आहे…' : 'OTP पडताळा'}
                      </button>
                    </div>
                  )}
                  {phoneVerification.message && <small className="profile-phone-message success">{phoneVerification.message}</small>}
                  {phoneVerification.error && <small className="profile-phone-message error" role="alert">{phoneVerification.error}</small>}
                  {profileForm.phone !== String(profile.phone || '') && !phoneVerification.token && (
                    <small>मोबाइल क्रमांक बदलण्यासाठी OTP पडताळणी आवश्यक आहे.</small>
                  )}
                </label>
                <div className="profile-field full"><Field label="प्रोफाइल फोटो URL" value={profileForm.avatar_url} onChange={value => setProfileForm({ ...profileForm, avatar_url: value })} placeholder="https://example.com/profile.jpg" /></div>
                <div className="profile-form-actions"><button className="profile-btn primary" disabled={loading}><ProfileIcon name="save" /> {loading ? 'जतन होत आहे...' : 'बदल जतन करा'}</button><button type="button" className="profile-btn secondary" onClick={() => applyProfile(profile)}><ProfileIcon name="reset" /> रीसेट करा</button></div>
              </form>
            </section>
            <AccountFacts profile={profile} tenant={tenant} roleLabel={roleLabel} />
          </>}

          {tab === 'security' && (
            <>
              <section className="profile-panel security-panel">
                <PanelTitle icon="shield" title="पासवर्ड आणि सुरक्षा" subtitle="तुमच्या खात्यासाठी मजबूत आणि वेगळा पासवर्ड वापरा" />
                <form className="password-form" onSubmit={changePassword}>
                  <Field label="सध्याचा पासवर्ड" required type="password" value={passwordForm.oldPassword} onChange={value => setPasswordForm({ ...passwordForm, oldPassword: value })} />
                  <Field label="नवीन पासवर्ड" required type="password" value={passwordForm.newPassword} onChange={value => setPasswordForm({ ...passwordForm, newPassword: value })} hint="किमान ६ अक्षरे; अक्षरे, अंक आणि विशेष चिन्हांचा वापर करा." />
                  <Field label="नवीन पासवर्ड पुन्हा" required type="password" value={passwordForm.confirmPassword} onChange={value => setPasswordForm({ ...passwordForm, confirmPassword: value })} />
                  <div className="profile-form-actions"><button className="profile-btn primary" disabled={loading}><ProfileIcon name="shield" /> {loading ? 'अपडेट होत आहे...' : 'पासवर्ड बदला'}</button></div>
                </form>
              </section>

              <section className="profile-panel" style={{ border: '1px solid #fca5a5', background: '#fff5f5' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 6px 0', color: '#991b1b', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span aria-hidden="true">⚠️</span> खाते कायमस्वरूपी हटवा (Delete Account)
                    </h3>
                    <p style={{ margin: 0, color: '#7f1d1d', fontSize: '0.88rem' }}>
                      आपले वापरकर्ता खाते आणि लॉगिन प्रवेश कायमस्वरूपी बंद करा.
                    </p>
                  </div>
                  <button
                    type="button"
                    style={{
                      background: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      padding: '10px 18px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.92rem',
                    }}
                    onClick={() => {
                      setShowDeleteModal(true);
                      setDeleteError('');
                      setDeletePassword('');
                      setDeleteReason('');
                      setDeleteConfirmed(false);
                    }}
                  >
                    🗑️ खाते हटवा
                  </button>
                </div>
              </section>
            </>
          )}

          {tab === 'account' && (
            <>
              <AccountFacts profile={profile} tenant={tenant} roleLabel={roleLabel} expanded />
              <section className="profile-panel" style={{ border: '1px solid #fca5a5', background: '#fff5f5', marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 6px 0', color: '#991b1b', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span aria-hidden="true">⚠️</span> खाते कायमस्वरूपी हटवा (Delete Account)
                    </h3>
                    <p style={{ margin: 0, color: '#7f1d1d', fontSize: '0.88rem' }}>
                      आपले वापरकर्ता खाते आणि लॉगिन प्रवेश कायमस्वरूपी बंद करा.
                    </p>
                  </div>
                  <button
                    type="button"
                    style={{
                      background: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      padding: '10px 18px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.92rem',
                    }}
                    onClick={() => {
                      setShowDeleteModal(true);
                      setDeleteError('');
                      setDeletePassword('');
                      setDeleteReason('');
                      setDeleteConfirmed(false);
                    }}
                  >
                    🗑️ खाते हटवा
                  </button>
                </div>
              </section>
            </>
          )}
        </main>

        <aside className="profile-aside">
          <section className="profile-side-card completion-card"><h3>प्रोफाइल पूर्णता</h3><div className="completion-summary"><div className="completion-ring" style={{ '--completion': `${completion * 3.6}deg` }}><span>{completion}%</span></div><p>{completion === 100 ? 'उत्तम! तुमचे प्रोफाइल पूर्ण आहे.' : 'उर्वरित माहिती भरून प्रोफाइल पूर्ण करा.'}</p></div>{completionItems.map(([label, done]) => <div className={`completion-item ${done ? 'done' : ''}`} key={label}><i>{done ? '✓' : '○'}</i><span>{label}</span></div>)}</section>
          <section className="profile-side-card"><h3>अलीकडील माहिती</h3><InfoRow label="शेवटचे लॉगिन" value={formatDate(profile.last_login, true)} /><InfoRow label="प्रोफाइल अपडेट" value={formatDate(profile.updatedAt, true)} /><InfoRow label="खाते तयार" value={formatDate(profile.createdAt)} /></section>
          <section className="profile-side-card"><h3>संपर्क पडताळणी</h3><VerifyRow icon="phone" label="मोबाइल क्रमांक" value={profileForm.phone || 'जोडलेला नाही'} verified={Boolean(profileForm.phone)} /><VerifyRow icon="mail" label="ईमेल पत्ता" value={profileForm.email || 'जोडलेला नाही'} verified={Boolean(profileForm.email)} /></section>
        </aside>
      </div>

      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            padding: '28px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                background: '#fee2e2',
                color: '#dc2626',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
              }}>
                ⚠️
              </div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>
                खाते कायमस्वरूपी हटवा?
              </h3>
            </div>

            <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '16px' }}>
              हे पाऊल उचलल्यास तुमचे वापरकर्ता नाव, पासवर्ड आणि सक्रिय सत्रे कायमस्वरूपी नष्ट केली जातील. महाराष्ट्र ग्रामपंचायत अधिनियम आणि शासकीय लेखापरीक्षण नियमांनुसार तुमचे कर व प्रमाणपत्र रेकॉर्ड वैधानिक सुरक्षिततेसाठी जतन राहतील.
            </p>

            {deleteError && (
              <div style={{
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '14px',
                fontSize: '0.88rem',
              }}>
                {deleteError}
              </div>
            )}

            <form onSubmit={handleDeleteAccount}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: '6px', color: '#334155' }}>
                  पुष्टीसाठी तुमचा पासवर्ड टाका <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="सध्याचा पासवर्ड टाका"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: '6px', color: '#334155' }}>
                  खाते हटवण्याचे कारण (पर्यायी)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="उदा. गाव बदलले, दुहेरी खाते इत्यादी."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '0.86rem', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={deleteConfirmed}
                    onChange={(e) => setDeleteConfirmed(e.target.checked)}
                    style={{ marginTop: '3px' }}
                    required
                  />
                  <span>
                    मी समजतो/समजते की हे खाते कायमस्वरूपी बंद होईल आणि परत सुरू करता येणार नाही.
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    color: '#334155',
                    padding: '10px 18px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  रद्द करा
                </button>
                <button
                  type="submit"
                  disabled={deleteLoading}
                  style={{
                    background: '#dc2626',
                    border: 'none',
                    color: '#ffffff',
                    padding: '10px 18px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: deleteLoading ? 'not-allowed' : 'pointer',
                    opacity: deleteLoading ? 0.7 : 1,
                  }}
                >
                  {deleteLoading ? 'हटवत आहे…' : 'कायमस्वरूपी खाते हटवा'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PanelTitle({ icon, title, subtitle }) { return <header className="profile-panel-title"><i><ProfileIcon name={icon} /></i><div><h2>{title}</h2><p>{subtitle}</p></div></header>; }
function Field({ label, required, hint, onChange, type = 'text', ...props }) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="profile-field">
      <span>{label}{required && <em> *</em>}</span>
      <div className={isPassword ? 'profile-password-input' : undefined}>
        <input
          {...props}
          type={isPassword && passwordVisible ? 'text' : type}
          onChange={onChange ? event => onChange(event.target.value) : undefined}
        />
        {isPassword && (
          <button
            type="button"
            className="profile-password-toggle"
            onClick={() => setPasswordVisible(visible => !visible)}
            aria-label={passwordVisible ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}
            aria-pressed={passwordVisible}
          >
            <ProfileIcon name={passwordVisible ? 'eye-off' : 'eye'} size={19} />
          </button>
        )}
      </div>
      {hint && <small>{hint}</small>}
    </label>
  );
}
function InfoRow({ label, value }) { return <div className="profile-info-row"><span>{label}</span><strong>{value}</strong></div>; }
function VerifyRow({ icon, label, value, verified }) { return <div className="verify-row"><i><ProfileIcon name={icon} size={16} /></i><div><span>{label}</span><strong>{value}</strong></div><em className={verified ? 'verified' : ''}>{verified ? 'पडताळलेले' : 'अपूर्ण'}</em></div>; }
function AccountFacts({ profile, tenant, roleLabel, expanded }) { const facts = [['भूमिका', roleLabel], ['खाते स्थिती', profile.is_active === false ? 'निष्क्रिय' : 'सक्रिय'], ['लॉगिन आयडी', profile.username || '—'], ['ग्रामपंचायत', tenant?.name || profile.Tenant?.name || 'प्रणाली स्तर'], ['वापरकर्ता क्रमांक', profile.id || '—'], ['खाते तयार', formatDate(profile.createdAt)]]; return <section className={`profile-panel facts-panel ${expanded ? 'expanded' : ''}`}><PanelTitle icon="building" title="अधिकृत खाते माहिती" subtitle="प्रणालीमध्ये नोंद असलेले प्रशासकीय तपशील" /><div className="profile-facts">{facts.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div></section>; }
